import { html, LitElement, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { WowFormController } from "ui-common";
import { appContext } from "ui-common/appContext";
import { rpc } from "ui-common/services/rpc";
import { sendChangeEmailVerificationlMutation } from "../../services/profile-mutations";
import { t, translate } from "../../translations/i18n";
import { changeEmailModalStyle } from "./change-email-modal.css";
import { NotificationService } from "ui-components/notification";
import "ui-components/button/wow-button";
import "ui-components/form-elements/input/wow-input";

// Form data interface for form fields only
interface EmailFormData {
  email: string;
}

@customElement("change-email-modal")
export class ChangeEmailModal extends LitElement {
  static override styles = [changeEmailModalStyle];

  @state()
  private currentStep = 0;

  @state()
  private isFormSubmitting = false;

  @state()
  private isOtpSubmitting = false;

  @state()
  private isResending = false;

  @state()
  userEmail: string = "";

  private newEmail: string = "";

  private emailFormController!: WowFormController<EmailFormData>;
  private otpFormController!: WowFormController<{ otpCode: string }>;

  override connectedCallback(): void {
    super.connectedCallback();
    this.initializeFormControllers();
  }

  private initializeFormControllers() {
    this.emailFormController = new WowFormController<EmailFormData>(
      this,
      {
        email: this.userEmail,
      },
      {
        validateOnBlur: true,
        validators: {
          email: [this.validateEmail.bind(this)],
        },
      },
      async (event) => {
        if (!event.isValid || !event.data) return;

        this.isFormSubmitting = true;

        try {
          const newEmail = event.data.email;
          this.newEmail = newEmail;

          await sendChangeEmailVerificationlMutation.mutate({
            email: newEmail,
            handle: appContext.currentUser?.handle || appContext.currentUser?.id || "",
          });

          // Move to next step
          const wizard = this.renderRoot.querySelector("wow-wizard");
          wizard?.next();
        } catch (error) {
          NotificationService.error(`${error}`);
          console.error("Error changing email:", error);
        } finally {
          this.isFormSubmitting = false;
        }
      },
      "#email-form",
    );

    this.otpFormController = new WowFormController<{ otpCode: string }>(
      this,
      { otpCode: "" },
      {
        validateOnBlur: true,
        validators: {
          otpCode: [this.validateOTPCode.bind(this)],
        },
      },
      async (event) => {
        if (!event.isValid || !event.data) return;

        this.isOtpSubmitting = true;

        try {
          await rpc("changeEmail", {
            email: this.newEmail,
            token: event.data.otpCode,
            userId: appContext.currentUser?.id || "",
          });

          NotificationService.success(translate("otp.verificationSuccess"));

          // Close the modal after successful verification
          this.handleModalClose();
        } catch (error) {
          NotificationService.error(translate("otp.invalidCode"));
          console.error("OTP verification error:", error);
        } finally {
          this.isOtpSubmitting = false;
        }
      },
      "#otp-form",
    );
  }

  private async validateEmail(value: string) {
    if (!value) {
      return translate("sign-up.emailRequired");
    }
    if (!/\S+@\S+\.\S+/.test(value)) {
      return translate("sign-up.invalidEmail");
    }
    return;
  }

  private async validateOTPCode(value: string) {
    if (!value) {
      return translate("otp.required");
    }
    if (value.length !== 6) {
      return translate("otp.invalidLength");
    }
    if (!/^[0-9a-z]+$/i.test(value)) {
      return translate("otp.invalidFormat");
    }
    return;
  }

  private get saveButtonText() {
    if (this.isFormSubmitting) return t("discounts.modal.saving");
    return t("editWorld.saveButton");
  }

  private handleModalClose = () => {
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  private async handleChangeEmail() {
    // Trigger form submission through form controller
    await this.emailFormController.submit();
  }

  private async handleResendOTP() {
    if (this.isResending) return;

    this.isResending = true;

    try {
      await rpc("resendOTP", {
        email: this.newEmail,
        userId: appContext.currentUser?.id || "",
        isEmailChange: true,
      });

      NotificationService.success("OTP resent successfully");
    } catch (error) {
      NotificationService.error(`${error}`);
      console.error("Error resending OTP:", error);
    } finally {
      this.isResending = false;
    }
  }

  private handleStepChange(e: CustomEvent<{ step: number }>) {
    this.currentStep = e.detail.step;
  }

  protected override render() {
    return html`<wow-modal
      variant="responsive"
      mobile-fullscreen
      @close=${this.handleModalClose}
      @footer-button-clicked=${this.handleChangeEmail}
    >
      <span slot="header">Change your email</span>
      ${this.currentStep === 0 ? html`<span slot="footer-button-text">${this.saveButtonText}</span>` : nothing}

      <div class="modal-content">
        <wow-wizard .currentStep=${this.currentStep} .totalSteps=${2} @step-change=${this.handleStepChange}>
          <div class="container" aria-label="step-0">
            <form class="email-form" id="email-form">
              <div class="form-layout">
                <wow-input name=${this.emailFormController.fieldNames.email} label="Email" required></wow-input>
              </div>
            </form>
          </div>
          <div class="container" aria-label="step-1">
            <div class="content">
              <div class="logo">
                <wow-logo></wow-logo>
              </div>

              <div class="verification-body">
                <h1 class="verification-title">${t("checkEmailForVerification.title")}</h1>
                <p class="verification-text">${t("checkEmailForVerification.welcome", { email: this.newEmail })}</p>
                <p class="verification-text">${t("checkEmailForVerification.checkEmail")}</p>

                <form class="otp-form" id="otp-form">
                  <div class="form-layout">
                    <wow-input
                      name=${this.otpFormController.fieldNames.otpCode}
                      label="${t("otp.enterCode")}"
                      placeholder="${t("otp.placeholder")}"
                      required
                      maxlength="6"
                      autocomplete="one-time-code"
                    ></wow-input>
                  </div>
                  <div class="form-actions">
                    <wow-button type="submit" form="otp-form" ?disabled=${this.isOtpSubmitting}>
                      ${this.isOtpSubmitting ? t("otp.verifying") : t("otp.verifyButton")}
                    </wow-button>
                    <wow-button
                      type="button"
                      variant="neutral"
                      ?disabled=${this.isResending}
                      @click=${this.handleResendOTP}
                    >
                      ${this.isResending ? "Resending..." : t("otp.resendCode")}
                    </wow-button>
                  </div>
                </form>

                <p class="verification-text italic">
                  <ph-warning></ph-warning> ${t("checkEmailForVerification.checkSpam")}
                </p>
                <p class="verification-text">${t("checkEmailForVerification.regards")}</p>
                <p class="verification-text">${t("checkEmailForVerification.theTeam")}</p>
              </div>
            </div>
          </div>
        </wow-wizard>
      </div>
    </wow-modal>`;
  }
}
